import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { ToastrService } from 'ngx-toastr';
import { ChatMessageType, MediaRecordStatus } from '../../../common/models';
import { VideoMessageLayout } from '../../models/video-layout.enum';
import { RecordedVideoMessageComponent } from '../recorded-video-message/recorded-video-message.component';
import { RECORD_STRATEGY } from '../../record-media.provider';
import { RecordStrategy } from '../../interfaces/record-strategy.interface';

@Component({
    selector: 'lib-media-message-recorder',
    templateUrl: './media-message-recorder.component.html',
    styleUrls: ['./media-message-recorder.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MediaMessageRecorderComponent implements OnInit, AfterViewInit {
  @Input() public type: ChatMessageType;

  @ViewChild('mediaElement') public mediaElement: ElementRef<HTMLMediaElement>;

  @ViewChild('recordedAudioElement') public recordedAudioElement: ElementRef<HTMLAudioElement>;
  @ViewChild(RecordedVideoMessageComponent) public recordedVideoElement: RecordedVideoMessageComponent;

  @Output() public mediaRecorded: EventEmitter<{ file: File, options?: { [key: string]: string } }> = new EventEmitter();

  public readonly maxDuration = 300;

  public status: MediaRecordStatus = MediaRecordStatus.INIT;
  public statuses: typeof MediaRecordStatus = MediaRecordStatus;

  public readonly messageTypes: typeof ChatMessageType = ChatMessageType;

  public timestamp = 0;

  public titleLabel: string;
  public startLabel: string;
  public readyLabel: string;
  public icon: string;

  public videoLayoutControl = new FormControl(VideoMessageLayout.ROUNDED);

  public readonly videoLayouts: typeof VideoMessageLayout = VideoMessageLayout;

  public videoLayoutOptions = [
    {
      icon: 'user-rectangle',
      type: VideoMessageLayout.LANDSCAPE
    },
    {
      icon: 'user-circle',
      type: VideoMessageLayout.ROUNDED
    },
    {
      icon: 'user-square',
      type: VideoMessageLayout.SQUARE
    },
  ]

  public deviceReady = false;
  public paused = false;
  public playing = true;

  constructor(
    @Inject(RECORD_STRATEGY) public recordStrategy: RecordStrategy,
    private readonly translateService: TranslateService,
    private readonly toastr: ToastrService,
  ) { }

  ngOnInit() {
    this.setLabels();
    this.checkBrowserSupport();
  }

  public setVideoLayout(value: VideoMessageLayout): void {
    this.videoLayoutControl.patchValue(value);
  }

  async ngAfterViewInit() {
    await this.recordStrategy.init(this.mediaElement.nativeElement, this.type);
  }

  ngOnDestroy() {
    this.resetPlayer();
    this.recordStrategy.destroy();
  }

  public emitMediaMessage(): void {
    const data = { file: this.recordStrategy.recordedData() as File };
    if (this.type === ChatMessageType.VIDEO_MESSAGE) {
      Object.assign(data, { options: { layout: this.videoLayoutControl?.value } });
    }

    this.mediaRecorded.emit(data);
  }

  public togglePlayPause(media: { paused: boolean, play: () => void, pause: () => void }): void {
    if (!media) {
      return;
    }

    if (media.paused) {
      media.play();
      return;
    }

    media.pause();
  }

  public resetPlayer(): void {
    this.recordStrategy.reset();
    this.paused = false;

    this.mediaElement.nativeElement.currentTime = 0;
  }

  public async startRecording(): Promise<void> {
    this.recordStrategy.start();

    if (this.paused) {
      this.recordStrategy.resume();
      this.paused = false;
      return;
    }
  }

  public async stopRecording(): Promise<void> {
    if (this.recordStrategy.status() !== MediaRecordStatus.RECORDING) {
      return;
    }

    await this.recordStrategy.stop();
  }

  public toggleRecordingPause(): void {
    if (this.paused) {
      this.paused = false;
      this.recordStrategy.resume();
      return;
    }

    this.paused = true;
    this.recordStrategy.pause();
  }

  private checkBrowserSupport(): void {
    if (this.type === ChatMessageType.VIDEO_MESSAGE && navigator.userAgent.match('CriOS')) {
      this.toastr.warning('Video recording may not work on Chrome / iOS due to a known error.');
      return;
    }

    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      return;
    }

    const error = this.translateService.instant('COMMON.browser_not_support_recording');
    this.toastr.error(error);
  }

  private setLabels(): void {
    switch (this.type) {
      case ChatMessageType.AUDIO_MESSAGE:
        this.titleLabel = 'CHAT.audio_message';
        this.startLabel = 'CHAT.record_instant_audio_message';
        this.readyLabel = 'CHAT.your_audio_message_ready';
        this.icon = 'microphone';
        break;
      default:
        this.titleLabel = 'CHAT.video_message';
        this.startLabel = 'CHAT.record_instant_video_message';
        this.readyLabel = 'CHAT.your_video_message_ready';
        this.icon = 'video-camera';
    }
  }
}
